import { useState, useEffect, useCallback, useMemo } from 'react'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
    Heart, Search, Filter, RefreshCw, ChevronDown,
    CheckCircle2, Clock, XCircle, Mail, Phone,
    MapPin, Users, MessageSquare, DollarSign,
    Eye, CheckCheck, AlertCircle, X, Loader2
} from 'lucide-react'

interface Application {
    id: string
    created_at: string
    applicant_name: string
    applicant_email: string
    phone: string | null
    country: string | null
    number_of_children: number | null
    children_ages: string | null
    hardship_type: 'full' | 'partial' | 'temporary'
    monthly_budget: string | null
    story: string
    application_type: 'homeschool' | 'elite' | 'bundle' | 'university'
    referral_source: string | null
    status: 'pending' | 'approved' | 'rejected' | 'waitlisted'
    admin_notes: string | null
    contacted: boolean
    granted_plan_key: string | null
    granted_at: string | null
    cause_area: string
    reviewed_by?: string | null
    reviewed_at?: string | null
    degree_interest?: string | null
    gift_enrolment_id?: string | null
}

const STATUS_ICONS = {
    pending: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
    approved: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    rejected: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
    waitlisted: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: AlertCircle },
}

export function HardshipApplicationsAdmin() {
    const { t } = useTranslation()
    const supabase = createClientComponentClient()
    const [apps, setApps] = useState<Application[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatus] = useState<string>('all')
    const [typeFilter, setType] = useState<string>('all')
    const [selected, setSelected] = useState<Application | null>(null)
    const [saving, setSaving] = useState(false)
    const [notes, setNotes] = useState('')
    const [newStatus, setNewStatus] = useState<string>('')
    const [grantPlan, setGrantPlan] = useState('')

    const statusLabels = useMemo(() => ({
        pending: t('hardship.admin.status.pending', 'Pending'),
        approved: t('hardship.admin.status.approved', 'Approved'),
        rejected: t('hardship.admin.status.rejected', 'Rejected'),
        waitlisted: t('hardship.admin.status.waitlisted', 'Waitlisted'),
    }), [t])

    const typeLabels = useMemo(() => ({
        homeschool: t('hardship.plans.homeschool', 'Standard Homeschool'),
        elite: t('hardship.plans.elite', 'Elite Academy'),
        bundle: t('hardship.plans.bundle', 'Bundle'),
        university: t('hardship.plans.university', 'University'),
    }), [t])

    const hardshipLabels = useMemo(() => ({
        full: t('hardship.step1.types.full.label', 'Full Scholarship'),
        partial: t('hardship.step1.types.partial.label', 'Partial Subsidy'),
        temporary: t('hardship.step1.types.temporary.label', 'Temporary Relief'),
    }), [t])

    const grantPlanLabels = useMemo(() => ({
        homeschool_single: t('hardship.admin.detail.grantPlans.homeschool_single', 'Homeschool Single'),
        homeschool_family: t('hardship.admin.detail.grantPlans.homeschool_family', 'Homeschool Family'),
        edu_elite: t('hardship.admin.detail.grantPlans.edu_elite', 'Elite Academy'),
        edu_bundle: t('hardship.admin.detail.grantPlans.edu_bundle', 'Bundle'),
    }), [t])

    const fetchApps = useCallback(async () => {
        setLoading(true)
        try {
            let q = supabase
                .from('scholarship_applications')
                .select('*')
                .eq('cause_area', 'hardship')
                .order('created_at', { ascending: false })

            if (statusFilter !== 'all') q = q.eq('status', statusFilter)
            if (typeFilter !== 'all') q = q.eq('application_type', typeFilter)

            const { data, error } = await q
            if (error) throw error
            setApps((data || []) as unknown as Application[])
        } catch (err) {
            console.error('Error fetching hardship applications:', err)
        } finally {
            setLoading(false)
        }
    }, [supabase, statusFilter, typeFilter])

    useEffect(() => { fetchApps() }, [fetchApps])

    const filtered = apps.filter(a =>
        a.applicant_name.toLowerCase().includes(search.toLowerCase()) ||
        a.applicant_email.toLowerCase().includes(search.toLowerCase()) ||
        (a.country || '').toLowerCase().includes(search.toLowerCase())
    )

    function openDetail(app: Application) {
        setSelected(app)
        setNotes(app.admin_notes || '')
        setNewStatus(app.status)
        setGrantPlan(app.granted_plan_key || '')
    }

    async function saveReview() {
        if (!selected) return
        setSaving(true)
        try {
            const update: any = {
                admin_notes: notes || null,
                status: newStatus,
                contacted: true,
            }
            if (newStatus === 'approved') {
                update.granted_plan_key = grantPlan || null
                update.granted_at = new Date().toISOString()
                // If granting access, update the user's subscription directly
                if (grantPlan) {
                    await supabase
                        .from('users')
                        .update({ active_plan: grantPlan, subscription_status: 'premium' })
                        .eq('email', selected.applicant_email)
                }
            }
            const { error } = await supabase
                .from('scholarship_applications')
                .update(update)
                .eq('id', selected.id)
            if (error) throw error
            setSelected(null)
            fetchApps()
        } catch (err) {
            console.error('Error saving review:', err)
        } finally {
            setSaving(false)
        }
    }

    async function markContacted(id: string) {
        await (supabase.from('scholarship_applications') as any).update({ contacted: true }).eq('id', id)
        fetchApps()
    }

    // Stats
    const pending = apps.filter(a => a.status === 'pending').length
    const approved = apps.filter(a => a.status === 'approved').length
    const uncontacted = apps.filter(a => !a.contacted && a.status === 'pending').length

    return (
        <div className="p-8 space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h2 className="text-3xl font-black text-slate-900 flex items-center gap-3">
                        <Heart className="w-8 h-8 text-rose-500" />
                        {t('hardship.admin.title', 'Hardship Applications')}
                    </h2>
                    <p className="text-slate-500 font-medium mt-1">{t('hardship.admin.subtitle', 'Review and manage scholarship & subsidy requests')}</p>
                </div>
                <button
                    onClick={fetchApps}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    {t('hardship.admin.refresh', 'Refresh')}
                </button>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { label: t('hardship.admin.stats.pending', 'Pending Review'), value: pending, color: 'text-amber-600', bg: 'bg-amber-50  border-amber-100' },
                    { label: t('hardship.admin.stats.approved', 'Approved'), value: approved, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' },
                    { label: t('hardship.admin.stats.needsContact', 'Needs Contact'), value: uncontacted, color: 'text-rose-600', bg: 'bg-rose-50    border-rose-100' },
                ].map(s => (
                    <div key={s.label} className={`${s.bg} border rounded-2xl p-5`}>
                        <p className={`text-3xl font-black ${s.color}`}>{s.value}</p>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">{s.label}</p>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 bg-white/60 p-4 rounded-2xl border border-slate-200">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                        placeholder={t('hardship.admin.filters.searchPlaceholder', 'Search by name, email, country...')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={e => setStatus(e.target.value)}
                    className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 focus:outline-none"
                >
                    <option value="all">{t('hardship.admin.filters.statusAll', 'All Statuses')}</option>
                    <option value="pending">{t('hardship.admin.status.pending', 'Pending')}</option>
                    <option value="approved">{t('hardship.admin.status.approved', 'Approved')}</option>
                    <option value="rejected">{t('hardship.admin.status.rejected', 'Rejected')}</option>
                    <option value="waitlisted">{t('hardship.admin.status.waitlisted', 'Waitlisted')}</option>
                </select>
                <select
                    value={typeFilter}
                    onChange={e => setType(e.target.value)}
                    className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 focus:outline-none"
                >
                    <option value="all">{t('hardship.admin.filters.typeAll', 'All Plans')}</option>
                    <option value="homeschool">{t('hardship.plans.homeschool', 'Standard')}</option>
                    <option value="elite">{t('hardship.plans.elite', 'Elite')}</option>
                    <option value="bundle">{t('hardship.plans.bundle', 'Bundle')}</option>
                </select>
            </div>

            {/* Application list */}
            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3].map(i => <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20 bg-white/30 rounded-3xl border border-dashed border-slate-300">
                    <Heart className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-500 font-bold">{t('hardship.admin.empty', 'No applications found')}</p>
                    <p className="text-slate-400 text-sm">{t('hardship.admin.emptySub', 'Hardship requests will appear here')}</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <AnimatePresence>
                        {filtered.map((app) => {
                            const sc = STATUS_ICONS[app.status]
                            const StatusIcon = sc.icon
                            const statusLabel = statusLabels[app.status]
                            return (
                                <motion.div
                                    key={app.id}
                                    layout
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:border-emerald-200 transition-all cursor-pointer"
                                    onClick={() => openDetail(app)}
                                >
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-4 min-w-0">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center flex-shrink-0 font-black text-emerald-700 text-sm">
                                                {app.applicant_name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="font-bold text-slate-900 truncate">{app.applicant_name}</p>
                                                    {!app.contacted && app.status === 'pending' && (
                                                        <span className="px-2 py-0.5 bg-rose-100 text-rose-600 text-[10px] font-black rounded-full uppercase tracking-wider">
                                                            {t('hardship.admin.notContacted', 'Not contacted')}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-slate-400 font-medium">{app.applicant_email}{app.country ? ` · ${app.country}` : ''}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 flex-shrink-0">
                                            <div className="hidden sm:flex flex-col items-end gap-1">
                                                <span className="text-xs font-bold text-slate-500">{typeLabels[app.application_type]}</span>
                                                <span className="text-xs font-bold text-slate-400">{hardshipLabels[app.hardship_type]}</span>
                                            </div>
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold ${sc.color}`}>
                                                <StatusIcon size={12} />
                                                {statusLabel}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                                        <p className="text-xs text-slate-400 font-medium line-clamp-1 flex-1 mr-4 italic">
                                            "{app.story.substring(0, 120)}{app.story.length > 120 ? '...' : ''}"
                                        </p>
                                        <p className="text-xs text-slate-400 font-medium flex-shrink-0">
                                            {new Date(app.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </p>
                                    </div>
                                </motion.div>
                            )
                        })}
                    </AnimatePresence>
                </div>
            )}

            {/* Detail drawer / modal */}
            <AnimatePresence>
                {selected && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-end p-4"
                        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
                        onClick={e => e.target === e.currentTarget && setSelected(null)}
                    >
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', stiffness: 340, damping: 30 }}
                            className="bg-white rounded-[1.75rem] w-full max-w-lg h-full overflow-y-auto shadow-2xl"
                        >
                            <div className="sticky top-0 bg-white px-7 pt-7 pb-4 border-b border-slate-100 flex items-center justify-between z-10">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900">{selected.applicant_name}</h3>
                                    <p className="text-sm text-slate-400 font-medium">
                                        {new Date(selected.created_at).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                    </p>
                                </div>
                                <button onClick={() => setSelected(null)} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
                                    <X size={16} className="text-slate-500" />
                                </button>
                            </div>

                            <div className="px-7 py-6 space-y-6">
                                {/* Contact info */}
                                <div className="space-y-2">
                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('hardship.admin.detail.contact', 'Contact')}</p>
                                    <div className="space-y-1.5">
                                        <a href={`mailto:${selected.applicant_email}`} className="flex items-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700">
                                            <Mail size={14} /> {selected.applicant_email}
                                        </a>
                                        {selected.phone && <p className="flex items-center gap-2 text-sm text-slate-600"><Phone size={14} /> {selected.phone}</p>}
                                        {selected.country && <p className="flex items-center gap-2 text-sm text-slate-600"><MapPin size={14} /> {selected.country}</p>}
                                    </div>
                                </div>

                                {/* Application details */}
                                <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-0.5">{t('hardship.admin.detail.plan', 'Plan')}</p>
                                            <p className="font-bold text-slate-700">{typeLabels[selected.application_type]}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-0.5">{t('hardship.admin.detail.assistance', 'Assistance')}</p>
                                            <p className="font-bold text-slate-700">{hardshipLabels[selected.hardship_type]}</p>
                                        </div>
                                        {selected.number_of_children && (
                                            <div>
                                                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-0.5">{t('hardship.admin.detail.children', 'Children')}</p>
                                                <p className="font-bold text-slate-700">
                                                    {selected.number_of_children}{selected.children_ages ? ` (ages ${selected.children_ages})` : ''}
                                                </p>
                                            </div>
                                        )}
                                        {selected.monthly_budget && (
                                            <div>
                                                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-0.5">{t('hardship.admin.detail.afford', 'Can Afford')}</p>
                                                <p className="font-bold text-slate-700">{selected.monthly_budget}</p>
                                            </div>
                                        )}
                                        {selected.referral_source && (
                                            <div className="col-span-2">
                                                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-0.5">{t('hardship.admin.detail.referredBy', 'Referred By')}</p>
                                                <p className="font-bold text-slate-700">{selected.referral_source}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Story */}
                                <div>
                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                                        <MessageSquare size={11} /> {t('hardship.admin.detail.story', 'Their Story')}
                                    </p>
                                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{selected.story}</p>
                                    </div>
                                </div>

                                {/* Review actions */}
                                <div className="border-t border-slate-100 pt-5 space-y-4">
                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('hardship.admin.detail.review', 'Review')}</p>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('hardship.admin.detail.updateStatus', 'Update Status')}</label>
                                        <select
                                            value={newStatus}
                                            onChange={e => setNewStatus(e.target.value)}
                                            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                        >
                                            <option value="pending">{t('hardship.admin.status.pending', 'Pending')}</option>
                                            <option value="approved">{t('hardship.admin.status.approved', 'Approved')}</option>
                                            <option value="rejected">{t('hardship.admin.status.rejected', 'Rejected')}</option>
                                            <option value="waitlisted">{t('hardship.admin.status.waitlisted', 'Waitlisted')}</option>
                                        </select>
                                    </div>

                                    {newStatus === 'approved' && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                                            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('hardship.admin.detail.grantPlan', 'Grant Plan Key')}</label>
                                            <select
                                                value={grantPlan}
                                                onChange={e => setGrantPlan(e.target.value)}
                                                className="w-full px-3 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-sm font-semibold text-slate-700 focus:outline-none"
                                            >
                                                <option value="">{t('hardship.admin.detail.grantPlanSelect', 'Select plan to grant...')}</option>
                                                <option value="homeschool_single">{grantPlanLabels.homeschool_single}</option>
                                                <option value="homeschool_family">{grantPlanLabels.homeschool_family}</option>
                                                <option value="edu_elite">{grantPlanLabels.edu_elite}</option>
                                                <option value="edu_bundle">{grantPlanLabels.edu_bundle}</option>
                                            </select>
                                            <p className="text-xs text-emerald-600 font-medium mt-1">{t('hardship.admin.detail.grantPlanDesc', 'This will activate the plan on their account immediately.')}</p>
                                        </motion.div>
                                    )}

                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('hardship.admin.detail.adminNotes', 'Admin Notes (internal only)')}</label>
                                        <textarea
                                            value={notes}
                                            onChange={e => setNotes(e.target.value)}
                                            rows={3}
                                            placeholder={t('hardship.admin.detail.adminNotesPlaceholder', 'Add any notes for the team...')}
                                            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none"
                                        />
                                    </div>

                                    <div className="flex gap-3">
                                        {!selected.contacted && (
                                            <button
                                                onClick={() => markContacted(selected.id)}
                                                className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-600 font-bold text-sm rounded-xl hover:bg-slate-50 transition-all"
                                            >
                                                <CheckCheck size={14} /> {t('hardship.admin.detail.markContacted', 'Mark Contacted')}
                                            </button>
                                        )}
                                        <button
                                            onClick={saveReview}
                                            disabled={saving}
                                            className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2"
                                        >
                                            {saving ? t('hardship.admin.detail.saving', 'Saving...') : `✓ ${t('hardship.admin.detail.saveReview', 'Save Review')}`}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
